// ModuleScreen.tsx — a single module (chapter) and its ordered steps.
import { Screen } from '@/components/Screen';
import { Display, Eyebrow, Body, Pressable, Card } from '@/components/ui';
import { Icon } from '@/components/icons';
import { StepRow } from '@/components/StepRow';
import { ModuleProgress } from '@/components/ModuleProgress';
import { useProgress } from '@/hooks/useProgress';
import type { Programme, Step } from '@/types';
import { findModule, moduleStatus, stepStatus, isStepOpenable } from '@/services/contentProvider';

export function ModuleScreen({
  programme,
  moduleId,
  onBack,
  onOpenStep,
}: {
  programme: Programme;
  moduleId: string;
  onBack: () => void;
  onOpenStep: (step: Step) => void;
}) {
  const progress = useProgress();
  const module = findModule(programme, moduleId);
  if (!module) {
    onBack();
    return null;
  }
  const status = moduleStatus(programme, module, progress.state);
  const locked = status === 'soon';

  return (
    <Screen label={'Chapter ' + module.order}>
      <Pressable
        as="button"
        onClick={onBack}
        ariaLabel="Back to Journey"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          color: 'var(--ink-soft)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 14,
          padding: '4px 0',
          minHeight: 44,
          marginBottom: 12,
        }}
      >
        <Icon name="chevLeft" size={18} /> Journey
      </Pressable>
      <Eyebrow>
        Chapter {module.order}
        {status === 'current' ? ' · In progress' : ''}
      </Eyebrow>
      <div style={{ marginTop: 7, marginBottom: 8 }}>
        <Display parts={module.title} size={27} />
      </div>
      <Body dim size={14.5}>
        {module.description}
      </Body>
      {!locked && (
        <div style={{ marginTop: 14 }}>
          <ModuleProgress programme={programme} module={module} progress={progress.state} />
        </div>
      )}

      {locked && (
        <Card well pad={15} style={{ marginTop: 18, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--amber)', marginTop: 1 }}>
            <Icon name="leaf" size={19} />
          </span>
          <Body dim size={13.5}>
            This chapter opens as you move through the ones before it. There&apos;s no rush to reach it.
          </Body>
        </Card>
      )}

      <div style={{ marginTop: 18 }}>
        <Card pad="4px 14px">
          {module.steps.map((s, i) => (
            <StepRow
              key={s.id}
              step={s}
              status={stepStatus(programme, s, progress.state)}
              onOpen={() => onOpenStep(s)}
              last={i === module.steps.length - 1}
              disabled={locked || !isStepOpenable(programme, s, progress.state)}
              lockedUntilTomorrow={s.id.startsWith('sc-') && !isStepOpenable(programme, s, progress.state) && stepStatus(programme, s, progress.state) === 'current'}
            />
          ))}
        </Card>
      </div>
    </Screen>
  );
}
